import { ChangeEvent, useEffect, useState } from "react";
import { Autocomplete } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useAccessRight, useDebounce, useTranslate } from "@/hooks";
import { getIndexesList, selectIndexesList } from "@/store/resSlice";
import { OPERATION_SET_REPORT_LIST } from "@/constants/robotConfigLists";
import { RES } from "@/constants/accessRights";
import { IActionSetReport, TOperationSetReport } from "@/types/ses/sesItemConfigTypes";
import { CustomMenuItem, CustomTextField } from "@/ui";
import { NoOptionText, RenderOption } from "@/ui";
import { ISaveHandlerProps, ISetReportProps } from "./SetReport.props";

const SetReport = ({ action, idx, channel, isDisabled, handleSaveActionInStore }: ISetReportProps): JSX.Element => {
	const [inputIndex, setInputIndex] = useState<string>(action.index); // индекс
	const [inputField, setInputField] = useState<string>(action.name); // поле
	const [inputOperationReport, setInputOperationReport] = useState<TOperationSetReport>(action.operation); // операция
	const [inputValue, setInputValue] = useState<string>(action.value); // значение

	const dispatch = useAppDispatch();
	const indexesList = useAppSelector(selectIndexesList); // store - список индексов и полей

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста
	const debouncedInputValue = useDebounce(inputValue, 1000); // задержка обработки значения

	useEffect(() => {
		// есть доступ и пустой список
		if (isAccess(RES.CUSTOM_FIELDS) && Object.keys(indexesList.data).length === 0) {
			dispatch(getIndexesList()); // получаем список индексов и полей
		}
	}, []);

	// следим за значением
	useEffect(() => {
		if (debouncedInputValue !== action.value) {
			setReportActionDataChangeHandler({ value: debouncedInputValue });
		}
	}, [debouncedInputValue]);

	// обработчик изменения индекса
	const handleIndexChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null): void => {
		const formattedValue = value || "";
		setInputIndex(formattedValue);
		setInputField(""); // сброс Поля
		action.index !== formattedValue && setReportActionDataChangeHandler({ index: formattedValue, field: "" });
	};

	// обработчик изменения поля
	const handleFieldChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null): void => {
		const formattedValue = value || "";
		setInputField(formattedValue);
		action.name !== formattedValue && setReportActionDataChangeHandler({ field: formattedValue });
	};

	// обработчик изменения операции
	const handleOperationChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const value = e.target.value as TOperationSetReport;
		setInputOperationReport(value);
		action.operation !== value && setReportActionDataChangeHandler({ operation: value });
	};

	// обработчик изменения данных действия "Отправка значения в отчетность" (setreport)
	const setReportActionDataChangeHandler = ({ index, field, operation, value }: Partial<ISaveHandlerProps>): void => {
		const data: { channel: string; idx: number; action: Partial<IActionSetReport> } = {
			channel,
			idx,
			action: { index: index ?? inputIndex, name: field ?? inputField, operation: operation ?? inputOperationReport, value: value ?? inputValue },
		};
		handleSaveActionInStore(data);
	};

	return (
		<>
			{/* индекс */}
			<Autocomplete
				fullWidth
				autoHighlight
				options={Object.keys(indexesList.data)}
				value={inputIndex}
				onChange={handleIndexChange}
				disabled={isDisabled}
				title={inputIndex}
				noOptionsText={<NoOptionText />}
				renderInput={(params) => <CustomTextField {...params} label={translate("input_index")} error={!inputIndex} />}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>

			{/* поле */}
			<Autocomplete
				fullWidth
				autoHighlight
				options={indexesList.data?.[inputIndex] || []}
				value={inputField}
				onChange={handleFieldChange}
				disabled={isDisabled}
				title={inputField}
				noOptionsText={<NoOptionText />}
				renderInput={(params) => <CustomTextField {...params} label={translate("input_field")} error={!inputField} />}
				renderOption={(props, option) => {
					return (
						<RenderOption {...props} key={option}>
							{option}
						</RenderOption>
					);
				}}
			/>

			{/* операция */}
			<CustomTextField select value={inputOperationReport} onChange={handleOperationChange} disabled={isDisabled} sx={{ flexShrink: 0, width: "60px" }}>
				{OPERATION_SET_REPORT_LIST.map(({ operation, translation }) => (
					<CustomMenuItem key={operation} value={operation}>
						{translation}
					</CustomMenuItem>
				))}
			</CustomTextField>

			{/* значение */}
			<CustomTextField
				fullWidth
				label={translate("input_value")}
				value={inputValue}
				onChange={(e) => setInputValue(e.target.value)}
				error={!inputValue}
				disabled={isDisabled}
				title={inputValue}
			/>
		</>
	);
};

export default SetReport;
